<template>
    <div
        class="o-leave-impersonate"
        :class="{ 'o-leave-impersonate--dragging': isDragging }"
        :style="rootStyle"
    >
        <spin-loader
            v-if="processing"
            class="c-spin-loader-overlay"
        ></spin-loader>
        <transition name="slide-width">
            <button
                v-show="!open"
                ref="tab"
                type="button"
                class="o-leave-impersonate__tab u-center u-primary-color__bg"
                @click="handleTabClick"
                @pointerdown="startDrag"
            >
                <i class="far fa-mask"></i>
            </button>
        </transition>
        <transition name="slide-width">
            <div
                v-show="open"
                ref="wrap"
                class="o-leave-impersonate__wrap"
            >
                <div
                    class="o-leave-impersonate__container u-center u-primary-color__border"
                    @pointerdown="startDrag"
                >
                    <button
                        type="button"
                        class="o-leave-impersonate__hide"
                        data-no-drag
                        @click="open = false"
                    >
                        <i class="fal fa-chevron-double-left u-hover--grow u-primary-color__text--hover">
                        </i>
                    </button>
                    <span
                        v-if="isNovaImpersonating"
                        class="o-leave-impersonate__logo o-leave-impersonate__label u-primary-color__text"
                    >
                        {{ 'NOVA' }}
                    </span>
                    <div class="o-leave-impersonate__text">
                        <span class="o-leave-impersonate__label">
                            {{ impersonateTypeText }}
                        </span>
                        <span class="o-leave-impersonate__title">
                            {{ userName }}
                        </span>
                    </div>
                    <base-button
                        class="o-leave-impersonate__leave"
                        data-no-drag
                        :text="leaveButtonText"
                        color="primary"
                        shade="lighter"
                        icon="fa-sign-out-alt"
                        @click="exitImpersonate"
                    ></base-button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import EzekiaLocalStorage from '@js/core/EzekiaLocalStorage.js';
import providesImpersonate from '@js/vue-mixins/providesImpersonate.js';
import {
    beginAuthenticationTransition,
    endAuthenticationTransition,
} from '@js/core/authenticationTransition.js';

const DEFAULT_TOP_OFFSET = 100;
const EDGE_PADDING = 8;
const DRAG_THRESHOLD = 4;
const STORAGE_KEY = 'leave-impersonate-top-offset';

export default {
    name: 'LeaveImpersonate',
    mixins: [providesImpersonate],
    props: {
        impersonatorId: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            open: true,
            processing: false,
            topOffset: DEFAULT_TOP_OFFSET,
            isDragging: false,
            suppressTabClick: false,
            dragPointerId: null,
            dragStartClientY: 0,
            dragStartTopOffset: DEFAULT_TOP_OFFSET,
        };
    },
    computed: {
        rootStyle() {
            return {
                top: `${this.topOffset}px`,
            };
        },
        userName() {
            return this.nal.user.fullname;
        },
        leaveButtonText() {
            return this.isImpersonating
                ? trans('user-impersonate.leave')
                : trans('user-impersonate.stop-impersonate');
        },
        impersonateTypeText() {
            return this.isImpersonating
                ? trans('user-impersonate.impersonating') + ':'
                : trans('user-impersonate.nova-impersonating') + ':';
        },
    },
    methods: {
        handleTabClick() {
            if (this.suppressTabClick) {
                this.suppressTabClick = false;
                return;
            }

            this.open = !this.open;
        },
        handleResize() {
            this.clampTopOffset();
            this.saveTopOffset();
        },
        restoreTopOffset() {
            const savedTopOffset = parseInt(EzekiaLocalStorage.get(STORAGE_KEY), 10);

            if (!Number.isNaN(savedTopOffset)) {
                this.topOffset = savedTopOffset;
            }

            this.$nextTick(() => {
                this.clampTopOffset();
                this.saveTopOffset();
            });
        },
        saveTopOffset() {
            EzekiaLocalStorage.set(STORAGE_KEY, this.topOffset);
        },
        startDrag(event) {
            if (event.button !== undefined && event.button !== 0) {
                return;
            }

            if (event.target.closest('[data-no-drag]')) {
                return;
            }

            this.dragPointerId = event.pointerId;
            this.dragStartClientY = event.clientY;
            this.dragStartTopOffset = this.topOffset;
            this.isDragging = false;

            if (event.currentTarget?.setPointerCapture) {
                event.currentTarget.setPointerCapture(event.pointerId);
            }

            window.addEventListener('pointermove', this.handleDrag);
            window.addEventListener('pointerup', this.endDrag);
            window.addEventListener('pointercancel', this.endDrag);
        },
        handleDrag(event) {
            if (event.pointerId !== this.dragPointerId) {
                return;
            }

            const nextTopOffset = this.dragStartTopOffset + (event.clientY - this.dragStartClientY);

            if (Math.abs(event.clientY - this.dragStartClientY) >= DRAG_THRESHOLD) {
                this.isDragging = true;
                this.suppressTabClick = true;
            }

            this.topOffset = this.getClampedTopOffset(nextTopOffset);
        },
        endDrag(event) {
            if (event.pointerId !== this.dragPointerId) {
                return;
            }

            this.stopDragListeners();
            this.saveTopOffset();

            if (this.isDragging) {
                window.setTimeout(() => {
                    this.suppressTabClick = false;
                }, 0);
            } else {
                this.suppressTabClick = false;
            }

            this.isDragging = false;
            this.dragPointerId = null;
        },
        stopDragListeners() {
            window.removeEventListener('pointermove', this.handleDrag);
            window.removeEventListener('pointerup', this.endDrag);
            window.removeEventListener('pointercancel', this.endDrag);
        },
        clampTopOffset() {
            this.topOffset = this.getClampedTopOffset(this.topOffset);
        },
        getClampedTopOffset(topOffset) {
            const componentHeight = this.open
                ? (this.$refs.wrap?.offsetHeight || 0)
                : (this.$refs.tab?.offsetHeight || 0);
            const maxTopOffset = Math.max(EDGE_PADDING, window.innerHeight - componentHeight - EDGE_PADDING);

            return Math.min(Math.max(topOffset, EDGE_PADDING), maxTopOffset);
        },
        async exitImpersonate() {
            this.processing = true;
            if (this.isImpersonating) {
                beginAuthenticationTransition();
                try {
                    await this.get('/api/delegate/leave', null, {
                        handleAuthenticationError: true,
                    });
                    location.reload();
                } catch (e) {
                    endAuthenticationTransition();
                    let message = trans('nal-lang.error.general-error');
                    if (e.status === 422) {
                        message = _(e.responseJSON.errors).values().first()[0];
                    }
                    notify(message, 'alert', true, 5000);
                    this.processing = false;
                    //return location.reload();
                }
            } else {
                window.location.href = '/stop-impersonate';
            }
        },
    },
    watch: {
        open() {
            this.$nextTick(() => {
                this.clampTopOffset();
                this.saveTopOffset();
            });
        },
    },
    mounted() {
        this.restoreTopOffset();
        window.addEventListener('resize', this.handleResize);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.handleResize);
        this.stopDragListeners();
    },
};

</script>

<style lang="scss" scoped>

.o-leave-impersonate {
    left: 0;
    position: fixed;
    z-index: 9999;

    &--dragging {
        user-select: none;
    }

    &__tab {
        appearance: none;
        border: 0;
        border-bottom-right-radius: 0.5rem;
        border-top-right-radius: 0.5rem;
        color: #fff;
        cursor: grab;
        flex-shrink: 0;
        font-size: 26px;
        height: 46px;
        left: 0;
        padding: 0;
        position: absolute;
        top: 0;
        touch-action: none;
        width: 46px;

        &:hover {
            .o-leave-impersonate__tab-icon {
                transform: scale(1.1);
            }
        }
    }

    &__wrap {
        max-width: 220px;
        width: 100%;
    }

    &__container {
        background-color: #fff;
        border-bottom-right-radius: 0.5rem;
        border-right: 2px solid;
        border-top-right-radius: 0.5rem;
        box-shadow: 0 2px 4px 0 rgb(0 0 0 / 5%);
        cursor: grab;
        display: flex;
        flex-direction: column;
        padding: 30px;
        touch-action: none;
    }

    &__text {
        display: flex;
        flex-direction: column;
        text-align: center;
    }

    &__title {
        font-size: 15px;
        font-weight: 600;
    }

    &__label {
        color: #aaa;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
    }

    &__leave {
        margin-top: 10px;
    }

    &__hide {
        align-self: flex-end;
        appearance: none;
        background: transparent;
        border: 0;
        cursor: pointer;
        font-size: 20px;
        line-height: 1;
        margin-right: -20px;
        margin-top: -20px;
    }

    &__logo {
        align-self: flex-start;
        margin-left: -20px;
        margin-top: -20px;
    }
}

.slide-width-enter {
    width: 0;
}

.slide-width-enter-active {
    overflow-x: hidden;
    transition: width 0.4s ease;
    white-space: nowrap;
}

.slide-width-leave-active {
    overflow-x: hidden;
    transition: width 0.4s ease;
    white-space: nowrap;
    width: 0;
}

</style>
